import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEntitlement from "../hooks/useEntitlement";
import { getRelevantQuestions, getQuestionBankVersion } from "../lib/riskQuestionSet";
import { AUDIT_QUESTIONS as LEGACY_AUDIT_QUESTIONS_2026_1 } from "../data/questionsLegacy2026_1";

import Sidebar from "../components/Sidebar";
import RemediationFindingCard from "../components/RemediationFindingCard";

import {
  readAuditSnapshotIndex,
  getAuditSnapshotPayload,
  readRemediationContext as readStoredRemediationContext,
  writeRemediationContext as persistRemediationContext,
  getActiveBusiness,
  getLatestSnapshotForBusiness,
  getCanonicalRemediationContext,
  ACTIVE_BIZ_KEY,
  REMEDIATION_CONTEXT_KEY,
} from "../lib/auditHistory";

import {
  readRemediationMap,
  writeRemediationMap,
  cryptoRandomId,
  downloadJson,
} from "../lib/remediationSnapshot";

import {
  getCachedFxRates,
  convertMoney,
  guessCurrencySymbol,
} from "../lib/fxRates";

import {
  uploadEvidenceFile,
  createSignedEvidenceUrl,
  removeEvidenceFile,
} from "../lib/evidenceStorage";

import {
  buildRemediationForFinding,
} from "../lib/remediationPlaybooks";

import { downloadRemediationReportPdf, downloadEvidenceDefenseReportPdf } from "../utils/pdfExport";
import { computeGovernanceAdjustedExposure } from "../lib/governanceAdjustedExposure.js";

const NET_METHOD_KEY = "aifine_net_method_v1";
const DEFAULT_NET_METHOD = "STRICT";
const SNAPSHOT_INDEX_KEY_PREFIX = "aifine_audit_snapshot_index";
const PUBLIC_AUDIT_RESULTS_KEY = "aifine_public_audit_results";
const PUBLIC_AUDIT_RESULTS_BIZ_PREFIX = "aifine_public_audit_results_business_";
const REM_PRIMARY_CCY_KEY = "aifine_remediation_primary_currency_v1";
const REM_FX_MODE_KEY = "aifine_remediation_fx_mode_v1";
const BUSINESS_PAYLOAD_PREFIX = "aifine_business_payload_";
const CONFIG_SHIM_KEY = "aifine_config";

function getRiskGrade(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "—";
  if (n <= 15) return "A";
  if (n <= 30) return "B";
  if (n <= 50) return "C";
  if (n <= 75) return "D";
  return "E";
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeObj(x) {
  return x && typeof x === "object" && !Array.isArray(x) ? x : {};
}

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function asString(x) {
  return String(x ?? "").trim();
}

function upper(x) {
  return String(x || "").trim().toUpperCase();
}

function normalizeStatus(s) {
  const v = String(s || "OPEN").toUpperCase();
  if (v === "OPEN" || v === "IN_PROGRESS" || v === "MITIGATED" || v === "CLOSED") return v;
  return "OPEN";
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtMoneyWhole(n, symbol) {
  const v = Number(n);
  if (!Number.isFinite(v)) return `${symbol || "$"}0`;
  return `${symbol || "$"}${Math.round(v).toLocaleString("en-US")}`;
}

function tryReadJsonLS(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function tryWriteJsonLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function uniqueStrings(items = []) {
  return Array.from(new Set(safeArray(items).map((item) => String(item || "").trim()).filter(Boolean)));
}

function normalizeStateCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function normalizeSelectedStates(values = []) {
  return uniqueStrings(values.map(normalizeStateCode).filter(Boolean));
}

function readBusinessPayloadLocal(businessId) {
  const bid = asString(businessId);
  if (!bid) return null;
  return tryReadJsonLS(`${BUSINESS_PAYLOAD_PREFIX}${bid}`, null);
}

function readConfigShimLocal() {
  return tryReadJsonLS(CONFIG_SHIM_KEY, null);
}

function extractSelectedStatesForExport(results, businessId) {
  const r = safeObj(results);
  const candidates = [
    r?.selectedStates,
    r?.selected_states,
    r?.summary?.selectedStates,
    r?.summary?.selected_states,
    r?.config?.selectedStates,
    r?.config?.selected_states,
    r?.meta?.selectedStates,
    r?.meta?.selected_states,
    readBusinessPayloadLocal(businessId)?.selectedStates,
    readBusinessPayloadLocal(businessId)?.selected_states,
    readConfigShimLocal()?.selectedStates,
    readConfigShimLocal()?.selected_states,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeSelectedStates(candidate);
    if (normalized.length) return normalized;
  }

  return [];
}

function normalizeJurisdictionTokenForExport(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");
}

function isUsStatePlaceholder(value) {
  const token = normalizeJurisdictionTokenForExport(value).replace(/[_-]/g, " ");
  return token === "US STATE" || token === "US STATES" || token === "US (STATE)";
}

function isUsFederalToken(value) {
  const token = normalizeJurisdictionTokenForExport(value).replace(/[_-]/g, " ");
  return token === "US" || token === "US FED" || token === "US FEDERAL" || token === "US (FED)";
}

function isAllJurisdictionsToken(value) {
  const token = normalizeJurisdictionTokenForExport(value).replace(/[_-]/g, " ");
  return token === "ALL" || token === "ALL JURISDICTIONS" || token === "GLOBAL";
}

function formatJurisdictionTokenForExport(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (isUsFederalToken(raw)) return "US";
  if (isUsStatePlaceholder(raw)) return "US (STATE)";
  const code = normalizeStateCode(raw);
  if (code) return code;
  return raw;
}

function resolveJurisdictionsForExport({ findingJurisdictions = [], summaryJurisdictions = [], selectedStates = [] } = {}) {
  const normalizedStates = normalizeSelectedStates(selectedStates);
  const rawFinding = uniqueStrings(findingJurisdictions);
  const rawSummary = uniqueStrings(summaryJurisdictions);
  const source = rawFinding.length ? rawFinding : rawSummary;

  const output = [];
  const push = (value) => {
    const clean = String(value || "").trim();
    if (!clean || output.includes(clean)) return;
    output.push(clean);
  };

  const expandedSummary = rawSummary.length ? rawSummary : source;

  source.forEach((item) => {
    if (isAllJurisdictionsToken(item)) {
      expandedSummary.forEach((summaryItem) => {
        if (isAllJurisdictionsToken(summaryItem)) return;
        if (isUsStatePlaceholder(summaryItem) && normalizedStates.length) {
          normalizedStates.forEach(push);
          return;
        }
        push(formatJurisdictionTokenForExport(summaryItem));
      });
      return;
    }

    if (isUsStatePlaceholder(item)) {
      if (normalizedStates.length) normalizedStates.forEach(push);
      else push("US (STATE)");
      return;
    }

    push(formatJurisdictionTokenForExport(item));
  });

  if (!output.length && normalizedStates.length) normalizedStates.forEach(push);
  return output;
}

function getActiveBusinessIdSafe() {
  try {
    return asString(getActiveBusiness()?.business_id);
  } catch {
    return "";
  }
}

function setNetMethodSafe(v) {
  try {
    localStorage.setItem(NET_METHOD_KEY, String(v || DEFAULT_NET_METHOD).toUpperCase());
  } catch {}
}

function extractAuditFxCache(results) {
  const r = safeObj(results);
  const candidates = [
    r?.fx_cache,
    r?.fxCache,
    r?.meta?.fx_cache,
    r?.meta?.fxCache,
    r?.summary?.fx_cache,
    r?.summary?.fxCache,
    r?.risk_exposure?.fx_cache,
    r?.risk_exposure?.fxCache,
    r?.risk_exposure?.fx,
    r?.risk_exposure?.fx_rates_cache,
  ];

  for (const c of candidates) {
    const o = safeObj(c);
    const rates = o?.rates && typeof o.rates === "object" ? o.rates : null;
    const base = o?.base ? upper(o.base) : "";
    const hasRates = Boolean(rates && Object.keys(rates).length);
    if (hasRates && base) {
      return {
        version: o?.version || o?.cache_version || "fx.snapshot",
        source_label: o?.source_label || o?.source || "Snapshot FX",
        retrieved_at_iso: o?.retrieved_at_iso || o?.retrievedAt || null,
        as_of_iso: o?.as_of_iso || o?.asOf || o?.date || null,
        base,
        rates,
      };
    }
  }

  return null;
}

function extractCombinedByCurrency(results) {
  const r = safeObj(results);
  const rx = safeObj(r.risk_exposure);
  return rx?.combined_by_currency && typeof rx.combined_by_currency === "object"
    ? rx.combined_by_currency
    : null;
}

function extractGrossBaselineSingleCurrency(results) {
  const r = safeObj(results);
  const rx = safeObj(r.risk_exposure);

  const headline = safeObj(rx.headline);
  const headlineGross = Number(headline.combined_structured_worst_case_native);
  if (Number.isFinite(headlineGross) && headlineGross >= 0) return headlineGross;

  const byCcy = safeObj(rx.combined_by_currency);
  const codes = Object.keys(byCcy || {}).filter(Boolean);
  if (codes.length) {
    const b = safeObj(byCcy[codes[0]]);
    const g2 = Number(b.combined_structured_worst_case_native);
    if (Number.isFinite(g2) && g2 >= 0) return g2;
  }

  const s = safeObj(r.summary);
  const legacy =
    Number(s.gross_exposure_ceiling_usd) ||
    Number(s.gross_exposure_ceiling) ||
    Number(s.gross) ||
    Number(r.gross) ||
    0;

  return Number.isFinite(legacy) ? legacy : 0;
}

function extractCurrencySymbolSingleCurrency(results) {
  const r = safeObj(results);
  const rx = safeObj(r.risk_exposure);
  const headline = safeObj(rx.headline);
  if (headline.currency_symbol) return String(headline.currency_symbol);

  const byCcy = safeObj(rx.combined_by_currency);
  const codes = Object.keys(byCcy || {}).filter(Boolean);
  if (codes.length) {
    const b = safeObj(byCcy[codes[0]]);
    if (b.currency_symbol) return String(b.currency_symbol);
  }

  const s = safeObj(r.summary);
  if (s.currency_symbol) return String(s.currency_symbol);
  return "$";
}

function extractBusinessName(results) {
  const r = safeObj(results);
  if (r.entity_name) return String(r.entity_name);
  const s = safeObj(r.summary);
  if (s.entity_name) return String(s.entity_name);
  const cfg = safeObj(r.config);
  if (cfg.clientName) return String(cfg.clientName);
  return "Your Business";
}

function inferPlaybookIdForFinding(findingLike = {}) {
  const finding = safeObj(findingLike);
  const explicitPlaybookId = upper(finding?.playbook_id || finding?.playbookId);
  if (explicitPlaybookId) return explicitPlaybookId;

  const idRaw = String(finding?.question_id ?? finding?.source_question_id ?? finding?.id ?? "");
  const numericQuestionId = Number(String(idRaw).replace(/[^0-9]/g, ""));
  const text = [finding?.title, finding?.text, finding?.guidance, finding?.category]
    .map((x) => String(x || "").toLowerCase())
    .join(" | ");

  const explicitMap = {
    1: "BIOMETRIC_IDENTIFICATION",
    2: "SOCIAL_SCORING",
    3: "BIOMETRIC_IDENTIFICATION",
    4: "MANIPULATIVE_DECEPTIVE_AI",
    5: "BIOMETRIC_IDENTIFICATION",
    6: "VULNERABLE_PERSONS",
    7: "SOCIAL_SCORING",
    8: "BIOMETRIC_IDENTIFICATION",
    9: "MANIPULATIVE_DECEPTIVE_AI",
    10: "TRANSPARENCY_DISCLOSURE",
    11: "AI_RISK_MANAGEMENT",
    12: "HUMAN_OVERSIGHT",
    13: "HUMAN_OVERSIGHT",
    14: "AI_RISK_MANAGEMENT",
    15: "HUMAN_OVERSIGHT",
    16: "AI_RISK_MANAGEMENT",
    17: "HUMAN_OVERSIGHT",
    18: "DATA_PROTECTION_PRIVACY",
    19: "HUMAN_OVERSIGHT",
    20: "HUMAN_OVERSIGHT",
    21: "DATA_PROTECTION_PRIVACY",
    22: "INCIDENT_REPORTING",
    23: "MONITORING_DRIFT",
    24: "DATASET_BIAS",
    25: "ACCOUNTABILITY_GOVERNANCE",
    26: "MODEL_DOCUMENTATION",
    27: "AUDIT_READINESS",
    28: "SECURITY_ROBUSTNESS",
    29: "AI_RISK_MANAGEMENT",
    30: "MONITORING_DRIFT",
    31: "TRANSPARENCY_DISCLOSURE",
    32: "TRANSPARENCY_DISCLOSURE",
    33: "MODEL_DOCUMENTATION",
    34: "EXPLAINABILITY",
    35: "DATA_PROTECTION_PRIVACY",
    36: "TRANSPARENCY_DISCLOSURE",
    37: "TRANSPARENCY_DISCLOSURE",
    38: "MANIPULATIVE_DECEPTIVE_AI",
    39: "TRANSPARENCY_DISCLOSURE",
    40: "AI_RISK_MANAGEMENT",
    41: "MODEL_DOCUMENTATION",
    42: "CHANGE_MANAGEMENT",
    43: "RECORDKEEPING_RETENTION",
    44: "AUDIT_READINESS",
    45: "TRAINING_DATA_GOVERNANCE",
    46: "DATA_PROTECTION_PRIVACY",
    47: "USER_REDRESS",
    48: "SECURITY_ROBUSTNESS",
    49: "SECURITY_ROBUSTNESS",
    50: "SECURITY_ROBUSTNESS",
    51: "INCIDENT_REPORTING",
    52: "HUMAN_OVERSIGHT",
    53: "MODEL_TESTING_VALIDATION",
    54: "AI_RISK_MANAGEMENT",
    55: "TRANSPARENCY_DISCLOSURE",
    56: "USER_REDRESS",
    57: "THIRD_PARTY_VENDOR_RISK",
    58: "THIRD_PARTY_VENDOR_RISK",
    59: "MODEL_TESTING_VALIDATION",
    60: "SECURITY_ROBUSTNESS",
  };

  if (Number.isFinite(numericQuestionId) && explicitMap[numericQuestionId]) {
    return explicitMap[numericQuestionId];
  }

  if (text.includes("social scoring")) return "SOCIAL_SCORING";
  if (text.includes("biometric") || text.includes("facial") || text.includes("voiceprint")) return "BIOMETRIC_IDENTIFICATION";
  if (text.includes("dark pattern") || text.includes("deceptive") || text.includes("subliminal") || text.includes("manipulative")) {
    return "MANIPULATIVE_DECEPTIVE_AI";
  }
  if (text.includes("vulnerable") || text.includes("children") || text.includes("minors")) return "VULNERABLE_PERSONS";
  if (text.includes("privacy") || text.includes("data protection") || text.includes("dpia") || text.includes("ropa")) {
    return "DATA_PROTECTION_PRIVACY";
  }
  if (text.includes("human-in-the-loop") || text.includes("human override") || text.includes("employment") || text.includes("creditworthiness") || text.includes("medical")) {
    return "HUMAN_OVERSIGHT";
  }
  if (text.includes("monitoring") || text.includes("drift")) return "MONITORING_DRIFT";
  if (text.includes("security") || text.includes("prompt injection") || text.includes("weights") || text.includes("leakage")) {
    return "SECURITY_ROBUSTNESS";
  }
  if (text.includes("incident")) return "INCIDENT_REPORTING";
  if (text.includes("vendor") || text.includes("third-party") || text.includes("supplier")) return "THIRD_PARTY_VENDOR_RISK";
  if (text.includes("disclosure") || text.includes("notice") || text.includes("transparent") || text.includes("watermark")) {
    return "TRANSPARENCY_DISCLOSURE";
  }
  if (text.includes("explain")) return "EXPLAINABILITY";
  if (text.includes("appeal") || text.includes("complaint") || text.includes("redress")) return "USER_REDRESS";
  if (text.includes("documentation") || text.includes("model card") || text.includes("technical documentation")) {
    return "MODEL_DOCUMENTATION";
  }
  if (text.includes("change management")) return "CHANGE_MANAGEMENT";
  if (text.includes("record") || text.includes("retention")) return "RECORDKEEPING_RETENTION";
  if (text.includes("testing") || text.includes("validation") || text.includes("hallucination") || text.includes("accuracy")) {
    return "MODEL_TESTING_VALIDATION";
  }
  if (text.includes("training data") || text.includes("provenance") || text.includes("dataset")) {
    return text.includes("bias") ? "DATASET_BIAS" : "TRAINING_DATA_GOVERNANCE";
  }
  if (text.includes("inventory")) return "AI_SYSTEM_INVENTORY";
  if (text.includes("governance") || text.includes("risk management") || text.includes("nist")) return "AI_RISK_MANAGEMENT";

  return "GENERAL_COMPLIANCE";
}

function enrichFindingWithPlaybook(rawFinding = {}, fallbackJurisdictions = []) {
  const finding = safeObj(rawFinding);
  const jurisdictions = Array.isArray(finding?.jurisdictions) && finding.jurisdictions.length
    ? finding.jurisdictions
    : safeArray(fallbackJurisdictions);

  const enrichedSeed = {
    ...finding,
    jurisdictions,
    playbook_id: finding?.playbook_id || inferPlaybookIdForFinding(finding),
  };

  const playbook = buildRemediationForFinding(enrichedSeed, {
    jurisdictions,
    gravity: finding?.gravity,
  });

  return {
    ...finding,
    jurisdictions,
    playbook_id: enrichedSeed.playbook_id,
    playbook,
  };
}

function extractFindings(results) {
  const r = safeObj(results);
  const fallbackJurisdictions = Array.isArray(r?.summary?.jurisdictions) ? r.summary.jurisdictions : [];
  const a = safeObj(r.assessment);
  const failedIds = new Set(safeArray(a.failed_question_ids).map((id) => String(id)));
  const unsureIds = new Set(safeArray(a.unsure_question_ids).map((id) => String(id)));

  const config = safeObj(r.config);
  const embeddedQuestionSnapshot = safeArray(a.question_snapshot);
  const snapshotQuestionBankVersion = String(a?.question_bank_version || r?.question_bank_version || "").trim();
  const currentQuestionBankVersion = String(getQuestionBankVersion() || "").trim();

  let questionBank = [];
  if (embeddedQuestionSnapshot.length) {
    // Preferred immutable source for 2026.2+ snapshots.
    questionBank = embeddedQuestionSnapshot;
  } else if (snapshotQuestionBankVersion && snapshotQuestionBankVersion === currentQuestionBankVersion) {
    questionBank = Array.isArray(getRelevantQuestions(config)) ? getRelevantQuestions(config) : [];
  } else {
    // Historical snapshots created before 2026.2 did not embed wording/version.
    // Preserve their original 2026.1 semantics instead of reinterpreting old
    // answers against the newly rewritten question bank.
    questionBank = Array.isArray(LEGACY_AUDIT_QUESTIONS_2026_1) ? LEGACY_AUDIT_QUESTIONS_2026_1 : [];
  }
  const questionMap = {};
  questionBank.forEach((q) => {
    if (q?.id == null) return;
    questionMap[String(q.id)] = q;
  });

  const activeQuestionIds = safeArray(a.active_question_ids)
    .map((id) => String(id))
    .filter(Boolean);

  const shouldBuildFullAssessmentSet =
    String(r?.source || "").toLowerCase() === "detailed_assessment" || activeQuestionIds.length > 0;

  if (shouldBuildFullAssessmentSet) {
    const actionableIdSet = new Set([
      ...Array.from(failedIds.values()),
      ...Array.from(unsureIds.values()),
    ]);

    const orderedIds = (activeQuestionIds.length
      ? activeQuestionIds.filter((qid) => actionableIdSet.has(String(qid)))
      : questionBank
          .map((q) => String(q?.id))
          .filter((qid) => actionableIdSet.has(String(qid))))
      .filter(Boolean);

    return orderedIds
      .map((qid) => {
        const q = safeObj(questionMap[String(qid)]);
        const questionText = String(q?.text || "").trim();
        const kind = failedIds.has(String(qid))
          ? "FAILED"
          : unsureIds.has(String(qid))
            ? "UNSURE"
            : "ANSWERED";

        const baseFinding = {
          id: `q_${qid}`,
          question_id: qid,
          source_question_id: qid,
          kind,
          category: q?.category || "Assessment",
          title: q?.title || `Question ${qid}`,
          text: questionText || `Question ${qid}`,
          gravity: Number.isFinite(Number(q?.gravity))
            ? Number(q.gravity)
            : kind === "UNSURE"
              ? 3
              : 5,
          jurisdictions: Array.isArray(q?.jurisdictions) && q.jurisdictions.length ? q.jurisdictions : fallbackJurisdictions,
          guidance: q?.guidance || "",
          playbook_id: inferPlaybookIdForFinding({ ...q, question_id: qid }),
        };

        return enrichFindingWithPlaybook(baseFinding, fallbackJurisdictions);
      })
      .filter((f) => f && f.id);
  }

  if (Array.isArray(r.findings) && r.findings.length) {
    return r.findings
      .map((f, idx) => {
        const o = safeObj(f);
        const baseFinding = {
          id: o.id ?? `finding_${idx}`,
          question_id: o.question_id ?? o.source_question_id ?? o.id ?? null,
          source_question_id: o.source_question_id ?? o.question_id ?? o.id ?? null,
          kind: o.kind ?? "FAILED",
          category: o.category ?? "—",
          title: o.title ?? o.text ?? "Finding",
          text: o.text ?? o.title ?? "",
          gravity: Number.isFinite(Number(o.gravity)) ? Number(o.gravity) : 5,
          jurisdictions: Array.isArray(o.jurisdictions) ? o.jurisdictions : fallbackJurisdictions,
          guidance: o.guidance ?? o.playbook ?? "",
          playbook_id: o.playbook_id || o.playbookId || null,
          playbook: safeObj(o.playbook),
        };
        return enrichFindingWithPlaybook(baseFinding, fallbackJurisdictions);
      })
      .filter((f) => f && f.id);
  }

  return [];
}

function stableFindingHash(row = {}) {
  const source = [row?.id, row?.title, row?.text, row?.category].filter(Boolean).join("|").toLowerCase();
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) % 1000003;
  }
  return hash;
}

function deriveFindingExposureWeight(row = {}) {
  const gravity = Number(row?.gravity || 0) || 0;
  const text = [row?.title, row?.text, row?.category, row?.guidance].filter(Boolean).join(" ").toLowerCase();

  let base = 1;
  if (gravity >= 10) base = 10;
  else if (gravity >= 8) base = 8;
  else if (gravity >= 6) base = 6;
  else if (gravity >= 4) base = 4;
  else if (gravity >= 2) base = 2;

  let multiplier = 1;
  if (/(biometric|social scoring|predictive policing|vulnerable|emotion recognition|deceptive|manipulative)/i.test(text)) {
    multiplier += 0.22;
  }
  if (/(medical|healthcare|critical infrastructure|creditworthiness|insurance|law enforcement|employment|firing|recruitment)/i.test(text)) {
    multiplier += 0.12;
  }
  if (/(privacy|data protection|dpia|ropa|sensitive data)/i.test(text)) {
    multiplier += 0.08;
  }
  if (/(security|adversarial|prompt injection|model weights|cybersecurity)/i.test(text)) {
    multiplier += 0.06;
  }
  if (/(transparency|disclosure|notice|explanation|instructions for deployers)/i.test(text)) {
    multiplier -= 0.05;
  }
  if (/(accountability|governance|inventory|change management|monitoring|recordkeeping|documentation)/i.test(text)) {
    multiplier -= 0.03;
  }

  const jurisdictions = Array.isArray(row?.jurisdictions) ? row.jurisdictions.length : 0;
  multiplier += Math.min(0.04, jurisdictions * 0.01);

  const hash = stableFindingHash(row);
  const tieBreaker = 1 + (((hash % 17) - 8) / 1000);

  return Math.max(0.1, base * multiplier * tieBreaker);
}

function allocateIntegerTotal(total, weights = []) {
  const safeTotal = Math.max(0, Math.round(Number(total || 0) || 0));
  if (!safeTotal || !Array.isArray(weights) || !weights.length) {
    return (Array.isArray(weights) ? weights : []).map(() => 0);
  }

  const normalized = weights.map((weight) => Math.max(0, Number(weight || 0) || 0));
  const sum = normalized.reduce((acc, value) => acc + value, 0);
  if (!sum) {
    const evenBase = Math.floor(safeTotal / normalized.length);
    const evenRemainder = safeTotal - evenBase * normalized.length;
    return normalized.map((_, index) => evenBase + (index < evenRemainder ? 1 : 0));
  }

  const raw = normalized.map((weight) => (weight / sum) * safeTotal);
  const floors = raw.map((value) => Math.floor(value));
  let remainder = safeTotal - floors.reduce((acc, value) => acc + value, 0);

  const order = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => {
      if (b.frac !== a.frac) return b.frac - a.frac;
      return a.index - b.index;
    });

  const output = [...floors];
  let cursor = 0;
  while (remainder > 0 && order.length) {
    output[order[cursor % order.length].index] += 1;
    remainder -= 1;
    cursor += 1;
  }

  return output;
}

function allocateExposureByFinding(gross, findings) {
  const list = safeArray(findings);
  if (!list.length) return {};
  const weights = list.map((finding) => deriveFindingExposureWeight(finding));
  const allocated = allocateIntegerTotal(gross, weights);
  const out = {};
  list.forEach((finding, idx) => {
    const id = String(finding?.id ?? "");
    if (!id) return;
    out[id] = allocated[idx] ?? 0;
  });
  return out;
}

function readRemediationContext() {
  return readStoredRemediationContext();
}

function writeRemediationContext(nextCtx) {
  return persistRemediationContext(nextCtx || {});
}

function ensureSnapshotScopedRemediationMap({ snapshotId, findings, existingMap } = {}) {
  const sid = asString(snapshotId);
  const sourceMap = safeObj(existingMap);
  if (!sid) {
    return { map: {}, changed: false };
  }

  const list = safeArray(findings);
  const nextMap = { ...sourceMap };
  let changed = false;

  const validIds = new Set(
    list
      .map((finding) => asString(finding?.id))
      .filter(Boolean)
  );

  Object.keys(nextMap).forEach((key) => {
    if (!validIds.has(asString(key))) {
      delete nextMap[key];
      changed = true;
    }
  });

  list.forEach((finding) => {
    const fid = asString(finding?.id);
    if (!fid) return;

    const current = safeObj(nextMap[fid]);
    const normalizedStatus = normalizeStatus(current?.status || "OPEN");
    const nextEntry = { ...current, status: normalizedStatus };

    const hadEntry = Object.prototype.hasOwnProperty.call(nextMap, fid);
    const currentStatus = asString(current?.status).toUpperCase();
    if (!hadEntry || currentStatus !== normalizedStatus) {
      changed = true;
    }

    nextMap[fid] = nextEntry;
  });

  return { map: nextMap, changed };
}

function resolveRemediationBootstrap(preferredBusinessId = "") {
  const active = getActiveBusiness();
  const fallbackBusinessId = asString(preferredBusinessId) || asString(active?.business_id);
  const storedCtx = readStoredRemediationContext();
  const resolvedBusinessId = asString(fallbackBusinessId) || asString(storedCtx?.business_id);

  const canonicalCtx = getCanonicalRemediationContext({
    businessId: resolvedBusinessId,
    preferredSnapshotId: storedCtx?.snapshot_id,
  });

  const latest = !asString(canonicalCtx?.snapshot_id)
    ? getLatestSnapshotForBusiness({ businessId: resolvedBusinessId })
    : null;

  const snapshotId =
    asString(canonicalCtx?.snapshot_id) ||
    asString(latest?.snapshot_id);

  const businessId =
    asString(canonicalCtx?.business_id) ||
    asString(latest?.business_id) ||
    resolvedBusinessId;

  const context = snapshotId
    ? getCanonicalRemediationContext({
        businessId,
        preferredSnapshotId: snapshotId,
      })
    : getCanonicalRemediationContext({ businessId });

  const results = loadAuditResults({ snapshotId, businessId });
  const findings = extractFindings(results);
  const existingMap = snapshotId ? readRemediationMap({ snapshotId }) || {} : {};
  const { map, changed } = ensureSnapshotScopedRemediationMap({
    snapshotId,
    findings,
    existingMap,
  });

  if (snapshotId && changed) {
    try {
      writeRemediationMap({ snapshotId, map });
    } catch {
      // ignore write failures; UI can still render OPEN defaults in memory
    }
  }

  try {
    if (context?.business_id || context?.snapshot_id) {
      writeRemediationContext(context);
    }
  } catch {
    // ignore context write failures
  }

  return {
    activeBizId: businessId,
    snapshotId,
    results,
    remediationMap: map,
    context,
  };
}

function loadAuditResults({ snapshotId, businessId }) {
  const sid = asString(snapshotId);
  if (sid) {
    const p = getAuditSnapshotPayload(sid);
    if (p && typeof p === "object") return p;
  }

  const bid = asString(businessId);
  if (bid) {
    const p2 = tryReadJsonLS(`${PUBLIC_AUDIT_RESULTS_BIZ_PREFIX}${bid}`, null);
    if (p2 && typeof p2 === "object") return p2;
  }

  const p3 = tryReadJsonLS(PUBLIC_AUDIT_RESULTS_KEY, null);
  if (p3 && typeof p3 === "object") return p3;

  return null;
}

function extractHeadlineMetricsForComparison(results) {
  const r = safeObj(results);
  const exposure = safeObj(r?.risk_exposure);
  const headline = safeObj(exposure?.headline);
  const byCurrency = safeObj(exposure?.combined_by_currency);
  const firstCode = Object.keys(byCurrency || {})[0] || "";
  const bucket = safeObj(byCurrency?.[firstCode]);
  const currencySymbol =
    headline?.currency_symbol || bucket?.currency_symbol || extractCurrencySymbolSingleCurrency(results) || "$";

  return {
    currencySymbol,
    high:
      Number(headline?.combined_structured_worst_case_native) ||
      Number(bucket?.combined_structured_worst_case_native) ||
      0,
    expected:
      Number(headline?.combined_net_native) ||
      Number(headline?.combined_net_planning_case_native) ||
      Number(bucket?.combined_net_native) ||
      0,
    lower:
      Number(headline?.combined_floor_native) ||
      Number(bucket?.combined_floor_native) ||
      0,
    riskScore: Number(r?.assessment?.severity_score_0_100) || null,
    complianceScore: Number(r?.assessment?.compliance_score_pct) || null,
  };
}

function buildPriorAuditComparison({ businessId, snapshotId, currentResults }) {
  try {
    const rows = Array.isArray(readAuditSnapshotIndex({ businessId })) ? readAuditSnapshotIndex({ businessId }) : [];
    const currentIdx = rows.findIndex((r) => String(r?.snapshot_id || "") === String(snapshotId || ""));
    if (currentIdx < 0) return null;
    const prevRow = rows[currentIdx + 1];
    if (!prevRow?.snapshot_id) return null;

    const prevPayload = getAuditSnapshotPayload(String(prevRow.snapshot_id));
    if (!prevPayload) return null;

    const current = extractHeadlineMetricsForComparison(currentResults);
    const prev = extractHeadlineMetricsForComparison(prevPayload);

    const buildDelta = (cur, prior) => {
      const c = Number(cur);
      const p = Number(prior);
      if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return null;
      const change = c - p;
      return {
        change,
        changeAbs: Math.abs(change),
        pct: Math.round((change / p) * 100),
        isDecrease: change < 0,
      };
    };

    return {
      currencySymbol: current.currencySymbol || "$",
      high: buildDelta(current.high, prev.high),
      expected: buildDelta(current.expected, prev.expected),
      lower: buildDelta(current.lower, prev.lower),
      previousSnapshotId: String(prevRow.snapshot_id),
    };
  } catch {
    return null;
  }
}

function getSnapshotDateLabel(results, snapshotId) {
  const candidates = [
    results?.created_at_iso,
    results?.createdAt,
    results?.snapshot_created_at_iso,
    results?.meta?.created_at_iso,
    results?.meta?.createdAt,
    results?.summary?.created_at_iso,
  ];

  for (const candidate of candidates) {
    const label = fmtDateOnly(candidate);
    if (label !== "—") return label;
  }

  if (snapshotId) {
    const numeric = Number(snapshotId);
    if (Number.isFinite(numeric) && numeric > 0) {
      const label = fmtDateOnly(numeric);
      if (label !== "—") return label;
    }
  }

  return "—";
}

function getFindingStatusRank(status) {
  const normalized = normalizeStatus(status);
  if (normalized === "OPEN") return 4;
  if (normalized === "IN_PROGRESS") return 3;
  if (normalized === "MITIGATED") return 2;
  if (normalized === "CLOSED") return 1;
  return 0;
}

function getFindingStableOrderValue(finding, fallbackIndex = 0) {
  const qid = Number(finding?.question_id ?? finding?.source_question_id ?? null);
  if (Number.isFinite(qid) && qid > 0) return qid;

  const fid = String(finding?.id ?? "");
  const fromId = Number(fid.replace(/[^0-9]/g, ""));
  if (Number.isFinite(fromId) && fromId > 0) return fromId;

  return Number(fallbackIndex) || 0;
}

function sortFindings(list, remediationMap, allocationById, sortBy, stableOrderMap = {}) {
  const rows = Array.isArray(list) ? [...list] : [];
  return rows.sort((a, b) => {
    const aId = String(a?.id ?? "");
    const bId = String(b?.id ?? "");
    const aStatus = normalizeStatus(safeObj(remediationMap?.[aId])?.status || "OPEN");
    const bStatus = normalizeStatus(safeObj(remediationMap?.[bId])?.status || "OPEN");
    const aAmount = Number(allocationById?.[aId]) || 0;
    const bAmount = Number(allocationById?.[bId]) || 0;
    const aGravity = Number(a?.gravity || 0) || 0;
    const bGravity = Number(b?.gravity || 0) || 0;
    const aStableOrder = Number(stableOrderMap?.[aId] ?? Number.MAX_SAFE_INTEGER);
    const bStableOrder = Number(stableOrderMap?.[bId] ?? Number.MAX_SAFE_INTEGER);

    if (sortBy === "STATUS") {
      const statusDiff = getFindingStatusRank(bStatus) - getFindingStatusRank(aStatus);
      if (statusDiff !== 0) return statusDiff;
      if (bAmount !== aAmount) return bAmount - aAmount;
      if (bGravity !== aGravity) return bGravity - aGravity;
      return aStableOrder - bStableOrder;
    }

    if (sortBy === "EXPOSURE") {
      if (bAmount !== aAmount) return bAmount - aAmount;
      if (bGravity !== aGravity) return bGravity - aGravity;
      return aStableOrder - bStableOrder;
    }

    if (bGravity !== aGravity) return bGravity - aGravity;
    if (bAmount !== aAmount) return bAmount - aAmount;
    return aStableOrder - bStableOrder;
  });
}

export default function RemediationHub() {
  const navigate = useNavigate();
  const { loading: entitlementLoading, isAuthenticated, allowRemediation } = useEntitlement();

  useEffect(() => {
    if (entitlementLoading) return;
    if (!isAuthenticated) {
      navigate("/auth", { replace: true });
      return;
    }
    if (!allowRemediation) {
      navigate("/pricing", { replace: true });
    }
  }, [entitlementLoading, isAuthenticated, allowRemediation, navigate]);

  const initialBootstrapRef = useRef(null);
  const mainScrollRef = useRef(null);
  if (!initialBootstrapRef.current) {
    initialBootstrapRef.current = resolveRemediationBootstrap();
  }
  const initialBootstrap = initialBootstrapRef.current;

  const [mode, setMode] = useState("EXECUTIVE");
  const [expandAllTick, setExpandAllTick] = useState(0);
  const [collapseAllTick, setCollapseAllTick] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("SEVERITY");

  const [activeBizId, setActiveBizId] = useState(() => initialBootstrap?.activeBizId || "");

  const [snapshotId, setSnapshotId] = useState(() => initialBootstrap?.snapshotId || "");

  const [results, setResults] = useState(() => initialBootstrap?.results || null);

  const [remediationMap, setRemediationMap] = useState(() => initialBootstrap?.remediationMap || {});

  const [primaryCurrency, setPrimaryCurrency] = useState(() => {
    try {
      return localStorage.getItem(REM_PRIMARY_CCY_KEY) || "";
    } catch {
      return "";
    }
  });

  const [fxMode, setFxMode] = useState(() => {
    try {
      const raw = localStorage.getItem(REM_FX_MODE_KEY) || "AUDIT_DATE";
      return upper(raw) === "CURRENT" ? "CURRENT" : "AUDIT_DATE";
    } catch {
      return "AUDIT_DATE";
    }
  });

  const [fxLive, setFxLive] = useState({
    status: "idle",
    cache: null,
    error: null,
  });

  useEffect(() => {
    const refreshFromStorage = () => {
      const next = resolveRemediationBootstrap(getActiveBusinessIdSafe());

      setActiveBizId(next?.activeBizId || "");
      setSnapshotId(next?.snapshotId || "");
      setResults(next?.results || null);
      setRemediationMap(next?.remediationMap || {});
      setNetMethodSafe("STRICT");
    };

    refreshFromStorage();

    const onStorage = (e) => {
      if (!e || e.key == null) return;
      const k = String(e.key);
      if (
        k === ACTIVE_BIZ_KEY ||
        k === REMEDIATION_CONTEXT_KEY ||
        k === NET_METHOD_KEY ||
        k.startsWith(SNAPSHOT_INDEX_KEY_PREFIX) ||
        k.startsWith("aifine_audit_snapshot_payload_snap_") ||
        k.startsWith("aifine_public_audit_results") ||
        k.startsWith("aifine_remediation_map_")
      ) {
        refreshFromStorage();
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", refreshFromStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", refreshFromStorage);
    };
  }, []);

  const writeTimerRef = useRef(null);
  useEffect(() => {
    const sid = asString(snapshotId);
    if (!sid) return;

    if (writeTimerRef.current) window.clearTimeout(writeTimerRef.current);
    writeTimerRef.current = window.setTimeout(() => {
      try {
        writeRemediationMap({ snapshotId: sid, map: remediationMap || {} });
      } catch {}
    }, 120);

    return () => {
      if (writeTimerRef.current) window.clearTimeout(writeTimerRef.current);
    };
  }, [snapshotId, remediationMap]);

  useEffect(() => {
    try {
      if (primaryCurrency) localStorage.setItem(REM_PRIMARY_CCY_KEY, String(primaryCurrency));
    } catch {}
  }, [primaryCurrency]);

  useEffect(() => {
    try {
      localStorage.setItem(REM_FX_MODE_KEY, String(fxMode || "AUDIT_DATE"));
    } catch {}
  }, [fxMode]);

  const findings = useMemo(() => extractFindings(results), [results]);
  const combinedByCurrency = useMemo(() => extractCombinedByCurrency(results), [results]);
  const applicableJurisdictions = useMemo(
    () => (Array.isArray(results?.summary?.jurisdictions) ? results.summary.jurisdictions : []),
    [results]
  );

  const exportSelectedStates = useMemo(
    () => extractSelectedStatesForExport(results, activeBizId),
    [results, activeBizId]
  );

  const currencyCodes = useMemo(() => {
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    return Object.keys(m || {}).filter(Boolean).map(upper).filter(Boolean);
  }, [combinedByCurrency]);

  const isMultiCurrency = currencyCodes.length > 1;

  const chosenPrimaryCode = useMemo(() => {
    if (!isMultiCurrency) return currencyCodes[0] || "";
    if (primaryCurrency && currencyCodes.includes(upper(primaryCurrency))) return upper(primaryCurrency);
    const headlineCode = upper(safeObj(safeObj(results)?.risk_exposure)?.headline?.currency_code);
    if (headlineCode && currencyCodes.includes(headlineCode)) return headlineCode;
    return currencyCodes[0] || "";
  }, [isMultiCurrency, currencyCodes, primaryCurrency, results]);

  const primarySymbol = useMemo(() => {
    if (!chosenPrimaryCode) return "$";
    if (!isMultiCurrency) return extractCurrencySymbolSingleCurrency(results);
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    const b = safeObj(m?.[chosenPrimaryCode]);
    const sym = b?.currency_symbol || safeObj(safeObj(results)?.risk_exposure)?.headline?.currency_symbol || null;
    return sym || guessCurrencySymbol(chosenPrimaryCode) || "$";
  }, [chosenPrimaryCode, isMultiCurrency, results, combinedByCurrency]);

  const fxAudit = useMemo(() => extractAuditFxCache(results), [results]);
  const auditFxAvailable = Boolean(fxAudit && fxAudit.rates && Object.keys(fxAudit.rates).length);

  useEffect(() => {
    if (!isMultiCurrency || !chosenPrimaryCode) {
      setFxLive((s) => ({ ...s, status: "idle", error: null }));
      return;
    }

    let alive = true;
    (async () => {
      try {
        setFxLive({ status: "loading", cache: null, error: null });
        const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: false });
        if (!alive) return;
        setFxLive({ status: "ready", cache, error: null });
      } catch (err) {
        if (!alive) return;
        setFxLive({
          status: "error",
          cache: null,
          error: String(err?.message || err || "FX error"),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [isMultiCurrency, chosenPrimaryCode]);

  const fxCacheInUse = useMemo(() => {
    if (!isMultiCurrency) return null;
    if (fxMode === "AUDIT_DATE" && auditFxAvailable) return fxAudit;
    if (fxMode === "CURRENT" && fxLive.status === "ready" && fxLive.cache) return fxLive.cache;
    if (fxLive.status === "ready" && fxLive.cache) return fxLive.cache;
    return null;
  }, [isMultiCurrency, fxMode, auditFxAvailable, fxAudit, fxLive]);

  const fxReadyForConversion = Boolean(isMultiCurrency && chosenPrimaryCode && fxCacheInUse);

  const missingFxFor = useMemo(() => {
    if (!fxReadyForConversion) return [];
    const miss = new Set();
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    for (const code of currencyCodes) {
      const b = safeObj(m?.[code]);
      const w = Number(b?.combined_structured_worst_case_native);
      if (!Number.isFinite(w)) continue;
      const conv = convertMoney(w, code, chosenPrimaryCode, fxCacheInUse);
      if (conv == null) miss.add(code);
    }
    return Array.from(miss);
  }, [fxReadyForConversion, combinedByCurrency, currencyCodes, chosenPrimaryCode, fxCacheInUse]);

  const businessName = useMemo(() => extractBusinessName(results), [results]);
  const riskScore = useMemo(() => Number(results?.assessment?.severity_score_0_100) || null, [results]);
  const riskGrade = useMemo(() => {
    const explicit = String(results?.assessment?.risk_grade || "").trim().toUpperCase();
    return /^[A-E]$/.test(explicit) ? explicit : getRiskGrade(riskScore);
  }, [results, riskScore]);
  const snapshotDateLabel = useMemo(() => getSnapshotDateLabel(results, snapshotId), [results, snapshotId]);
  const grossBaselineSingle = useMemo(() => extractGrossBaselineSingleCurrency(results), [results]);

  const grossBaseline = useMemo(() => {
    if (!isMultiCurrency) return grossBaselineSingle;
    if (!fxReadyForConversion) return 0;

    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    let total = 0;
    for (const code of currencyCodes) {
      const b = safeObj(m?.[code]);
      const w = Number(b?.combined_structured_worst_case_native);
      if (!Number.isFinite(w)) continue;
      const conv = convertMoney(w, code, chosenPrimaryCode, fxCacheInUse);
      if (typeof conv === "number") total += conv;
    }
    return Math.round(total);
  }, [
    isMultiCurrency,
    grossBaselineSingle,
    fxReadyForConversion,
    combinedByCurrency,
    currencyCodes,
    chosenPrimaryCode,
    fxCacheInUse,
  ]);

  const allocationById = useMemo(
    () => allocateExposureByFinding(grossBaseline, findings),
    [grossBaseline, findings]
  );

  const stableOrderMap = useMemo(() => {
    const out = {};
    findings.forEach((finding, idx) => {
      const fid = String(finding?.id ?? "");
      if (!fid) return;
      out[fid] = getFindingStableOrderValue(finding, idx);
    });
    return out;
  }, [findings]);

  const topRiskDrivers = useMemo(() => {
    return [...findings]
      .map((finding) => ({
        id: String(finding?.id ?? ""),
        title: String(finding?.title || finding?.text || "Finding"),
        text: String(finding?.text || "").trim(),
        amount: Number(allocationById?.[String(finding?.id ?? "")]) || 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);
  }, [findings, allocationById]);

  const visibleFindings = useMemo(() => {
    const filtered = findings.filter((finding) => {
      if (statusFilter === "ALL") return true;
      const findingId = String(finding?.id ?? "");
      const entry = safeObj(remediationMap?.[findingId]);
      return normalizeStatus(entry?.status || "OPEN") === statusFilter;
    });
    return sortFindings(filtered, remediationMap, allocationById, sortBy, stableOrderMap);
  }, [findings, statusFilter, remediationMap, allocationById, sortBy, stableOrderMap]);

  const statusCounts = useMemo(() => {
    const counts = { OPEN: 0, IN_PROGRESS: 0, MITIGATED: 0, CLOSED: 0 };
    findings.forEach((f) => {
      const id = String(f?.id ?? "");
      const entry = safeObj(remediationMap?.[id]);
      const st = normalizeStatus(entry?.status || "OPEN");
      counts[st] = (counts[st] || 0) + 1;
    });
    return counts;
  }, [findings, remediationMap]);

  const statusMoney = useMemo(() => {
    const sums = { OPEN: 0, IN_PROGRESS: 0, MITIGATED: 0, CLOSED: 0 };
    findings.forEach((f) => {
      const id = String(f?.id ?? "");
      const entry = safeObj(remediationMap?.[id]);
      const st = normalizeStatus(entry?.status || "OPEN");
      const amt = Number(allocationById?.[id]) || 0;
      sums[st] = (sums[st] || 0) + amt;
    });

    const reducedBy = (sums.CLOSED || 0) + (sums.MITIGATED || 0);
    const remaining = Math.max(0, (Number(grossBaseline) || 0) - reducedBy);
    return { sums, reducedBy, remaining };
  }, [findings, remediationMap, allocationById, grossBaseline]);


  const governanceRemediationSummary = useMemo(() => {
    const rows = findings.map((finding) => {
      const fid = String(finding?.id ?? "");
      const entry = safeObj(remediationMap?.[fid]);
      return {
        id: fid,
        title: finding?.title || finding?.text || "Finding",
        category: finding?.category || "—",
        gravity: Number(finding?.gravity || 0),
        status: normalizeStatus(entry?.status || "OPEN"),
        exposure_high: Math.round(Number(allocationById?.[fid]) || 0),
        exposure_expected: Math.round((Number(allocationById?.[fid]) || 0) * 0.7),
        evidence_links: safeArray(entry?.evidence_links),
        uploads: safeArray(entry?.uploads),
      };
    });

    return {
      rows,
      totalCount: rows.length,
      closedCount: rows.filter((row) => row.status === "CLOSED").length,
      mitigatedCount: rows.filter((row) => row.status === "MITIGATED").length,
      inProgressCount: rows.filter((row) => row.status === "IN_PROGRESS").length,
      openCount: rows.filter((row) => row.status === "OPEN").length,
      completeCount: rows.filter((row) => row.status === "CLOSED" || row.status === "MITIGATED").length,
      criticalOpenCount: rows.filter((row) => row.status !== "CLOSED" && Number(row.gravity || 0) >= 8).length,
      evidenceLinkedFindings: rows.filter((row) => safeArray(row.evidence_links).length || safeArray(row.uploads).length).length,
    };
  }, [findings, remediationMap, allocationById]);

  const governanceAdjustedExposure = useMemo(
    () =>
      computeGovernanceAdjustedExposure({
        exposure: results?.risk_exposure || null,
        canonicalPayload: results || null,
        remediationSummary: governanceRemediationSummary,
      }),
    [results, governanceRemediationSummary],
  );

  const hasResults = Boolean(results && typeof results === "object");
  const hasFindings = Array.isArray(findings) && findings.length > 0;

  const handleStatusChange = (findingId, nextStatus) => {
    const fid = String(findingId || "");
    if (!fid) return;
    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      return { ...cur, [fid]: { ...entry, status: normalizeStatus(nextStatus) } };
    });
  };

  const handleNotesChange = (findingId, notes) => {
    const fid = String(findingId || "");
    if (!fid) return;
    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      return { ...cur, [fid]: { ...entry, notes: String(notes || "") } };
    });
  };

  const handleEvidenceAdd = (findingId) => {
    const fid = String(findingId || "");
    if (!fid) return;
    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.evidence_links);
      const rid = cryptoRandomId("e");
      return { ...cur, [fid]: { ...entry, evidence_links: [...list, { id: rid, label: "", url: "", notes: "" }] } };
    });
  };

  const handleEvidenceChange = (findingId, rowId, nextRow) => {
    const fid = String(findingId || "");
    const rid = String(rowId || "");
    if (!fid || !rid) return;
    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.evidence_links);
      return {
        ...cur,
        [fid]: { ...entry, evidence_links: list.map((r) => (String(r?.id) === rid ? nextRow : r)) },
      };
    });
  };

  const handleEvidenceRemove = (findingId, rowId) => {
    const fid = String(findingId || "");
    const rid = String(rowId || "");
    if (!fid || !rid) return;
    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.evidence_links);
      return { ...cur, [fid]: { ...entry, evidence_links: list.filter((r) => String(r?.id) !== rid) } };
    });
  };

  const handleUploadAdd = (findingId, uploadMeta) => {
    const fid = String(findingId || "");
    if (!fid) return;
    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.uploads);
      return { ...cur, [fid]: { ...entry, uploads: [...list, safeObj(uploadMeta)] } };
    });
  };

  const handleUploadFile = async (findingId, file) => {
    const fid = String(findingId || "");
    if (!fid || !file) return;

    try {
      const uploaded = await uploadEvidenceFile(file, {
        businessId: activeBizId || "business_unknown",
        snapshotId: snapshotId || "snapshot_unknown",
        findingId: fid,
      });

      setRemediationMap((prev) => {
        const cur = safeObj(prev);
        const entry = safeObj(cur[fid]);
        const list = safeArray(entry.uploads);
        return { ...cur, [fid]: { ...entry, uploads: [...list, uploaded] } };
      });
    } catch (err) {
      alert(`Upload failed: ${String(err?.message || err || "Unknown error")}`);
    }
  };

  const handleUploadOpen = async (findingId, uploadId) => {
    const fid = String(findingId || "");
    const uid = String(uploadId || "");
    if (!fid || !uid) return;

    try {
      const entry = safeObj(remediationMap?.[fid]);
      const upload = safeArray(entry.uploads).find((u) => String(u?.id) === uid);
      const storageKey = String(upload?.storage_key || "");
      if (!storageKey) {
        alert("No storage key found for this upload.");
        return;
      }

      const url = await createSignedEvidenceUrl(storageKey, 3600);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      alert(`Could not open file: ${String(err?.message || err || "Unknown error")}`);
    }
  };

  const handleUploadRemove = async (findingId, uploadId) => {
    const fid = String(findingId || "");
    const uid = String(uploadId || "");
    if (!fid || !uid) return;

    const existingEntry = safeObj(remediationMap?.[fid]);
    const existingUpload = safeArray(existingEntry.uploads).find((u) => String(u?.id) === uid);
    const storageKey = String(existingUpload?.storage_key || "");

    if (storageKey) {
      try {
        await removeEvidenceFile(storageKey);
      } catch (err) {
        alert(`File metadata removed locally, but storage delete failed: ${String(err?.message || err || "Unknown error")}`);
      }
    }

    setRemediationMap((prev) => {
      const cur = safeObj(prev);
      const entry = safeObj(cur[fid]);
      const list = safeArray(entry.uploads);
      return { ...cur, [fid]: { ...entry, uploads: list.filter((u) => String(u?.id) !== uid) } };
    });
  };

  const handleInitializeFromPreviousSnapshot = () => {
    try {
      const ctx = readRemediationContext();
      const bid = ctx?.business_id ? String(ctx.business_id) : getActiveBusinessIdSafe();
      const index = readAuditSnapshotIndex({ businessId: bid }) || [];
      const rows = Array.isArray(index) ? index : [];
      const curSid = String(snapshotId || "");

      if (!curSid) {
        alert("No current snapshot selected for remediation.");
        return;
      }

      const curIdx = rows.findIndex((r) => String(r?.snapshot_id) === curSid);
      const prevRow = curIdx >= 0 ? rows[curIdx + 1] : rows[1];
      const prevSid = prevRow?.snapshot_id ? String(prevRow.snapshot_id) : "";

      if (!prevSid) {
        alert("No prior snapshot found for this business.");
        return;
      }

      const prevMap = readRemediationMap({ snapshotId: prevSid }) || {};
      writeRemediationMap({ snapshotId: curSid, map: prevMap });
      setRemediationMap(prevMap);
      alert("Initialized remediation state from the previous snapshot.");
    } catch (err) {
      alert(`Initialize failed: ${String(err?.message || err || "Unknown error")}`);
    }
  };

  const handleExportJson = () => {
    const fx = fxCacheInUse || null;
    const payload = {
      version: "1.1",
      created_at_iso: new Date().toISOString(),
      method: "STRICT",
      snapshot_id: snapshotId || null,
      business_id: activeBizId || null,
      entity_name: businessName,
      currency_code: isMultiCurrency ? chosenPrimaryCode : null,
      currency_symbol: primarySymbol,
      fx_mode: isMultiCurrency ? fxMode : null,
      fx_disclosure: isMultiCurrency
        ? {
            note: "Locked snapshot. FX mode affects display conversion only.",
            mode: fxMode,
            primary_currency: chosenPrimaryCode,
            source_label: fx?.source_label || null,
            as_of_iso: fx?.as_of_iso || null,
            retrieved_at_iso: fx?.retrieved_at_iso || null,
            base: fx?.base || null,
            audit_fx_present: auditFxAvailable,
            missing_rates_for: missingFxFor,
          }
        : null,
      gross_baseline: Math.round(Number(grossBaseline) || 0),
      remaining_baseline: Math.round(Number(statusMoney.remaining) || 0),
      remediation_map: remediationMap || {},
    };

    downloadJson("remediation_export", payload);
  };

  const handleDownloadRemediationPdf = (event) => {
    try {
      if (event?.preventDefault) event.preventDefault();
      if (event?.stopPropagation) event.stopPropagation();
      const summaryRows = findings.map((finding) => {
        const fid = String(finding?.id ?? "");
        const entry = safeObj(remediationMap?.[fid]);
        const amount = Number(allocationById?.[fid]) || 0;
        const status = String(entry?.status || "OPEN").toLowerCase();

        const exportJurisdictions = resolveJurisdictionsForExport({
          findingJurisdictions: safeArray(finding?.jurisdictions),
          summaryJurisdictions: applicableJurisdictions,
          selectedStates: exportSelectedStates,
        });

        return {
          id: fid,
          title: finding?.title || finding?.text || "Finding",
          text: finding?.text || "",
          category: finding?.category || "—",
          gravity: Number(finding?.gravity || 0),
          status,
          exposure_high: Math.round(amount),
          exposure_expected: Math.round(amount * 0.7),
          exposure_lower: Math.round(amount * 0.4),
          notes: String(entry?.notes || ""),
          evidence_links: safeArray(entry?.evidence_links),
          uploads: safeArray(entry?.uploads),
          jurisdictions: exportJurisdictions,
          playbook: safeObj(finding?.playbook),
        };
      });

      const remediationPayload = {
        snapshot: {
          snapshot_id: snapshotId || null,
          business_id: activeBizId || null,
          entity_name: businessName,
          scope_jurisdictions: applicableJurisdictions,
          selected_states: exportSelectedStates,
          assessment: {
            compliance_score_pct: Number(results?.assessment?.compliance_score_pct) || null,
            severity_score_0_100: Number(results?.assessment?.severity_score_0_100) || null,
          },
          payload: {
            gross_exposure: Math.round(Number(grossBaseline) || 0),
            net_exposure: Math.round(Number(statusMoney.remaining) || 0),
            weighted_credit_amount: Math.round(Number(statusMoney.reducedBy) || 0),
          },
          governance_adjusted_exposure: governanceAdjustedExposure || null,
          layer_3: governanceAdjustedExposure?.layer_3 || null,
        },
        remediationMap: Object.fromEntries(
          summaryRows.map((row) => [
            row.id,
            {
              title: row.title,
              text: row.text,
              category: row.category,
              gravity: row.gravity,
              jurisdictions: row.jurisdictions,
              status: row.status,
              exposure_high: row.exposure_high,
              exposure_expected: row.exposure_expected,
              exposure_lower: row.exposure_lower,
              notes: row.notes,
              evidence_links: row.evidence_links,
              uploads: row.uploads,
              playbook: row.playbook,
            },
          ])
        ),
      };

      const priorAuditComparison = buildPriorAuditComparison({
        businessId: activeBizId || "",
        snapshotId: snapshotId || "",
        currentResults: results,
      });

      downloadRemediationReportPdf({
        ...remediationPayload,
        prior_audit_comparison: priorAuditComparison || null,
      });
    } catch (err) {
      alert(`Remediation PDF export failed: ${String(err?.message || err || "Unknown error")}`);
    }
  };

  const handleDownloadEvidenceDefensePdf = (event) => {
    try {
      if (event?.preventDefault) event.preventDefault();
      if (event?.stopPropagation) event.stopPropagation();
      const summaryRows = findings.map((finding) => {
        const fid = String(finding?.id ?? "");
        const entry = safeObj(remediationMap?.[fid]);
        const amount = Number(allocationById?.[fid]) || 0;
        const status = String(entry?.status || "OPEN").toLowerCase();

        const exportJurisdictions = resolveJurisdictionsForExport({
          findingJurisdictions: safeArray(finding?.jurisdictions),
          summaryJurisdictions: applicableJurisdictions,
          selectedStates: exportSelectedStates,
        });

        return {
          id: fid,
          title: finding?.title || finding?.text || "Finding",
          text: finding?.text || "",
          category: finding?.category || "—",
          gravity: Number(finding?.gravity || 0),
          status,
          exposure_high: Math.round(amount),
          exposure_expected: Math.round(amount * 0.7),
          exposure_lower: Math.round(amount * 0.4),
          notes: String(entry?.notes || ""),
          evidence_links: safeArray(entry?.evidence_links),
          uploads: safeArray(entry?.uploads),
          jurisdictions: exportJurisdictions,
          playbook: safeObj(finding?.playbook),
        };
      });

      const remediationPayload = {
        snapshot: {
          snapshot_id: snapshotId || null,
          business_id: activeBizId || null,
          entity_name: businessName,
          scope_jurisdictions: applicableJurisdictions,
          selected_states: exportSelectedStates,
          assessment: {
            compliance_score_pct: Number(results?.assessment?.compliance_score_pct) || null,
            severity_score_0_100: Number(results?.assessment?.severity_score_0_100) || null,
          },
          payload: {
            gross_exposure: Math.round(Number(grossBaseline) || 0),
            net_exposure: Math.round(Number(statusMoney.remaining) || 0),
            weighted_credit_amount: Math.round(Number(statusMoney.reducedBy) || 0),
          },
          governance_adjusted_exposure: governanceAdjustedExposure || null,
          layer_3: governanceAdjustedExposure?.layer_3 || null,
        },
        remediationMap: Object.fromEntries(
          summaryRows.map((row) => [
            row.id,
            {
              id: row.id,
              question_id: row.id,
              source_question_id: row.id,
              question_number: row.id,
              title: row.title,
              text: row.text,
              category: row.category,
              gravity: row.gravity,
              jurisdictions: row.jurisdictions,
              status: row.status,
              exposure_high: row.exposure_high,
              exposure_expected: row.exposure_expected,
              exposure_lower: row.exposure_lower,
              notes: row.notes,
              evidence_links: row.evidence_links,
              uploads: row.uploads,
              playbook: row.playbook,
            },
          ])
        ),
      };

      downloadEvidenceDefenseReportPdf(remediationPayload);
    } catch (err) {
      alert(`Evidence & Defense PDF export failed: ${String(err?.message || err || "Unknown error")}`);
    }
  };

  const handlePrintReport = () => {
    try {
      window.print();
    } catch {}
  };

  const handleUpdateFxRates = async () => {
    if (!isMultiCurrency) return;
    try {
      setFxLive({ status: "loading", cache: null, error: null });
      const cache = await getCachedFxRates({ maxAgeHours: 12, forceRefresh: true });
      setFxLive({ status: "ready", cache, error: null });
    } catch (err) {
      setFxLive({
        status: "error",
        cache: null,
        error: String(err?.message || err || "FX error"),
      });
    }
  };

  const handleScrollToTop = () => {
    const scroller = mainScrollRef.current;
    if (scroller && typeof scroller.scrollTo === "function") {
      scroller.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const hasRemediationAccess = isAuthenticated && allowRemediation;

  useEffect(() => {
    if (asString(snapshotId)) return;

    try {
      const next = resolveRemediationBootstrap(getActiveBusinessIdSafe());
      if (asString(next?.snapshotId)) {
        setActiveBizId(next?.activeBizId || "");
        setSnapshotId(next.snapshotId);
        setResults(next?.results || null);
        setRemediationMap(next?.remediationMap || {});
      }
    } catch {}
  }, [snapshotId]);

  if (entitlementLoading) return null;
  if (!hasRemediationAccess) return null;

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main ref={mainScrollRef} className="flex-1 overflow-auto p-8 md:p-12">
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start">
            <div className="min-w-0 pr-0 xl:pr-8">
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-3xl font-extrabold tracking-tight">Remediation Hub</div>
                <span className="rounded-xl border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-black text-white shadow-sm">
                  Entity: <span className="font-black">{businessName}</span>
                </span>
              </div>
              <div className="mt-2 text-[15px] leading-7 text-slate-600 xl:max-w-none">
              <span className="block xl:whitespace-nowrap">
                Review the items below to see what action is needed to remediate identified issues. Update the status of each item as remediation progresses using the dropdown.
                </span>
                <span className="block xl:whitespace-nowrap">
                Upload supporting evidence where possible. Closed and mitigated items reduce remaining exposure.
                </span>
              </div>
              {isMultiCurrency ? (
                <div className="mt-3 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-black text-amber-900">
                  Multi-currency snapshot
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-start gap-2 xl:justify-end xl:self-start">
              <button
                type="button"
                onClick={(event) => handleDownloadEvidenceDefensePdf(event)}
                className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-black text-blue-700 hover:bg-blue-100"
              >
                Export Evidence &amp; Defense Report
              </button>

              <button
                type="button"
                onClick={(event) => handleDownloadRemediationPdf(event)}
                className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-black text-white hover:bg-blue-700"
              >
                Export Remediation Report
              </button>

              <button
                type="button"
                onClick={() => navigate("/reports")}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-black text-slate-700 hover:bg-slate-50"
              >
                Back to Reports
              </button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <div className="flex items-center gap-2">
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Mode</div>
              <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
                <button
                  type="button"
                  onClick={() => setMode("EXECUTIVE")}
                  className={cn(
                    "rounded-xl px-3 py-1.5 text-xs font-black",
                    mode === "EXECUTIVE" ? "bg-slate-900 text-white" : "text-slate-700"
                  )}
                >
                  Executive
                </button>
                <button
                  type="button"
                  onClick={() => setMode("LEGAL")}
                  className={cn(
                    "rounded-xl px-3 py-1.5 text-xs font-black",
                    mode === "LEGAL" ? "bg-slate-900 text-white" : "text-slate-700"
                  )}
                >
                  Legal
                </button>
              </div>
            </div>
          </div>

          {!hasResults ? (
            <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="text-lg font-black">No audit results found.</div>
              <div className="mt-2 text-sm text-slate-600">
                Remediation requires a stored assessment snapshot. Run an Assessment Audit, then use Reports →{" "}
                <span className="font-black">Go to Remediation</span>.
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/assessment")}
                  className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-black text-white hover:bg-slate-800"
                >
                  Go to Assessment →
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/reports")}
                  className="rounded-2xl border bg-white px-4 py-2 text-sm font-bold hover:border-slate-300"
                >
                  Back to Reports
                </button>
              </div>

              <div className="mt-10 pb-2 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </div>
          ) : (
            <>
              {isMultiCurrency ? (
                <details className="group mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="text-lg font-black text-slate-900">FX Conversion Settings</div>
                      <div className="mt-1 text-sm text-slate-600">
                        Your saved snapshot never changes. FX settings only change how totals are displayed in your selected currency.
                      </div>
                    </div>
                    <span className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-black text-slate-700">
                      <span className="group-open:hidden">Show settings</span>
                      <span className="hidden group-open:inline">Hide settings</span>
                    </span>
                  </summary>

                  <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full border bg-white px-3 py-1">
                          Primary currency: <span className="font-black">{chosenPrimaryCode || "—"}</span>
                        </span>

                        <div className="inline-flex items-center gap-2">
                          <span className="text-slate-600 font-bold">Choose primary currency:</span>
                          <select
                            value={
                              primaryCurrency && currencyCodes.includes(upper(primaryCurrency))
                                ? upper(primaryCurrency)
                                : chosenPrimaryCode
                            }
                            onChange={(e) => setPrimaryCurrency(String(e.target.value || ""))}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black"
                          >
                            {currencyCodes.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                        FX conversion mode
                      </div>

                      <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
                        <button
                          type="button"
                          onClick={() => setFxMode("AUDIT_DATE")}
                          className={cn(
                            "rounded-xl px-3 py-1.5 text-xs font-black",
                            fxMode === "AUDIT_DATE" ? "bg-slate-900 text-white" : "text-slate-700"
                          )}
                        >
                          Audit-date FX (default)
                        </button>
                        <button
                          type="button"
                          onClick={() => setFxMode("CURRENT")}
                          className={cn(
                            "rounded-xl px-3 py-1.5 text-xs font-black",
                            fxMode === "CURRENT" ? "bg-slate-900 text-white" : "text-slate-700"
                          )}
                        >
                          Current FX
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={handleUpdateFxRates}
                        className="rounded-2xl border bg-white px-4 py-2 text-xs font-black hover:bg-slate-50"
                      >
                        Update FX rates
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 text-[11px] text-slate-500">
                    <span className="font-black text-slate-700">FX disclosure:</span>{" "}
                    {fxMode === "AUDIT_DATE" ? (
                      auditFxAvailable ? (
                        <>
                          Using <span className="font-black">audit-date FX</span> embedded in this snapshot. Source:{" "}
                          <span className="font-black">{fxAudit?.source_label || "Snapshot FX"}</span>. As of{" "}
                          <span className="font-black">{fmtDateTime(fxAudit?.as_of_iso)}</span>.
                        </>
                      ) : (
                        <>
                          <span className="font-black text-amber-800">Audit-date FX not stored in this snapshot.</span>{" "}
                          Conversions fall back to <span className="font-black">Current FX</span> for display only.
                        </>
                      )
                    ) : fxLive.status === "ready" && fxLive.cache ? (
                      <>
                        Using <span className="font-black">Current FX</span>. Source:{" "}
                        <span className="font-black">{fxLive.cache.source_label}</span>. As of{" "}
                        <span className="font-black">{fmtDateTime(fxLive.cache.as_of_iso)}</span> (retrieved{" "}
                        <span className="font-black">{fmtDateTime(fxLive.cache.retrieved_at_iso)}</span>).
                      </>
                    ) : fxLive.status === "loading" ? (
                      <>Loading current FX rates…</>
                    ) : (
                      <>
                        <span className="font-black text-amber-800">Current FX unavailable.</span> Conversions may be incomplete.
                      </>
                    )}{" "}
                    <span className="text-slate-400">
                      Snapshot is locked; FX only affects converted display totals in {chosenPrimaryCode || "primary currency"}.
                    </span>
                    {missingFxFor?.length ? (
                      <>
                        {" "}<span className="text-amber-700 font-black">Note:</span> Missing FX rates for:{" "}
                        <span className="font-black">{missingFxFor.join(", ")}</span>. Those currencies are excluded from converted totals.
                      </>
                    ) : null}
                  </div>
                </details>
              ) : null}

              <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <div>
                    <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                      Potential Maximum Exposure → Remaining Exposure
                    </div>

                    <div className="mt-3 flex flex-wrap items-end gap-3">
                      <div className="text-4xl font-black text-red-700">
                        {fmtMoneyWhole(grossBaseline, primarySymbol)}
                      </div>
                      <div className="text-4xl font-black text-slate-300">→</div>
                      <div className="text-4xl font-black text-emerald-700">
                        {fmtMoneyWhole(statusMoney.remaining, primarySymbol)}
                      </div>
                    </div>

                    <div className="mt-2 text-xs text-slate-500">
                      STRICT baseline. Closed and mitigated items reduce remaining exposure; open and in-progress items remain at risk.
                      {isMultiCurrency ? (
                        <>
                          {" "}Converted display in <span className="font-black">{chosenPrimaryCode}</span> (Locked Snapshot unchanged).
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:min-w-[360px]">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="text-xs font-bold text-slate-500">Risk Score</div>
                      <div className="mt-1 text-3xl font-black text-slate-900">
                        {riskScore == null ? "—" : Math.round(riskScore)}
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="text-xs font-bold text-slate-500">Risk Grade</div>
                      <div className="mt-1 text-3xl font-black text-slate-900">{riskGrade}</div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <div className="text-xs font-bold text-slate-500">Snapshot</div>
                      <div className="mt-2 text-lg font-black text-slate-900">{snapshotDateLabel}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">Status Overview</div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-3xl border border-red-400 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">Open</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.OPEN || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Remaining: <span className="font-black">{fmtMoneyWhole(statusMoney.sums.OPEN, primarySymbol)}</span>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-amber-400 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">In Progress</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.IN_PROGRESS || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Remaining: <span className="font-black">{fmtMoneyWhole(statusMoney.sums.IN_PROGRESS, primarySymbol)}</span>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-blue-400 bg-white p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-900">Mitigated</div>
                    <div className="mt-2 text-3xl font-black">{statusCounts.MITIGATED || 0}</div>
                    <div className="mt-1 text-xs text-slate-600">
                      Exposure Reduced: <span className="font-black">{fmtMoneyWhole(statusMoney.sums.MITIGATED, primarySymbol)}</span>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-emerald-500 bg-emerald-50/40 p-5">
                    <div className="text-xs font-black uppercase tracking-wide text-emerald-900">Closed</div>
                    <div className="mt-2 text-3xl font-black text-slate-900">{statusCounts.CLOSED || 0}</div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-emerald-800">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-emerald-500 text-[10px] font-black">✓</span>
                      <span>
                        Exposure Reduced: <span className="font-black">{fmtMoneyWhole(statusMoney.sums.CLOSED, primarySymbol)}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-lg font-black text-slate-900">Top 3 Risk Drivers</div>
                <div className="mt-2 text-sm text-slate-600">
                  The three issues currently driving the largest remaining regulatory exposure.
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                  {topRiskDrivers.map((driver, idx) => (
                    <div key={driver.id || idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">Driver {idx + 1}</div>
                      <div className="mt-2 font-black text-slate-900">{driver.title}</div>
                      <div className="mt-2 text-xs text-slate-600 line-clamp-3">{driver.text || "No additional summary available."}</div>
                      <div className="mt-3 text-sm font-black text-red-700">{fmtMoneyWhole(driver.amount, primarySymbol)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm text-slate-600">
                    Showing <span className="font-black text-slate-900">{visibleFindings.length}</span> of <span className="font-black text-slate-900">{findings.length}</span> findings
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(String(e.target.value || "ALL"))}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700"
                    >
                      <option value="ALL">Filter: All</option>
                      <option value="OPEN">Filter: Open</option>
                      <option value="IN_PROGRESS">Filter: In Progress</option>
                      <option value="MITIGATED">Filter: Mitigated</option>
                      <option value="CLOSED">Filter: Closed</option>
                    </select>

                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(String(e.target.value || "SEVERITY"))}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700"
                    >
                      <option value="SEVERITY">Sort: Severity</option>
                      <option value="EXPOSURE">Sort: Exposure</option>
                      <option value="STATUS">Sort: Status</option>
                    </select>

                    <button
                      type="button"
                      onClick={() => setExpandAllTick((v) => v + 1)}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                    >
                      Expand all
                    </button>
                    <button
                      type="button"
                      onClick={() => setCollapseAllTick((v) => v + 1)}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-700 hover:border-slate-300"
                    >
                      Collapse all
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div className="text-lg font-black text-slate-900">Evidence &amp; Documentation</div>
                <div className="mt-2 text-sm text-slate-600 xl:whitespace-nowrap">
                  Attach documents or links that demonstrate remediation actions. All evidence is securely stored and supports regulatory defensibility.
                </div>
              </div>

              <div className="mt-8 space-y-4">
                {!hasFindings ? (
                  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm text-sm text-slate-600">
                    No findings found for this snapshot.
                  </div>
                ) : (
                  visibleFindings.map((finding, idx) => {
                    const fid = String(finding?.id ?? "");
                    const entry = safeObj(remediationMap?.[fid]);
                    const defaultExpandLegalDetails =
                      String(mode).toUpperCase() === "LEGAL" && idx < 3 && Number(finding?.gravity || 0) >= 7;

                    return (
                      <RemediationFindingCard
                        key={fid || idx}
                        finding={finding}
                        entry={entry}
                        statuses={[
                          { value: "OPEN", label: "Open" },
                          { value: "IN_PROGRESS", label: "In Progress" },
                          { value: "MITIGATED", label: "Mitigated" },
                          { value: "CLOSED", label: "Closed" },
                        ]}
                        mode={String(mode).toUpperCase()}
                        applicableJurisdictions={applicableJurisdictions}
                        onStatusChange={handleStatusChange}
                        onNotesChange={handleNotesChange}
                        onEvidenceAdd={handleEvidenceAdd}
                        onEvidenceChange={handleEvidenceChange}
                        onEvidenceRemove={handleEvidenceRemove}
                        onUploadAdd={handleUploadAdd}
                        onUploadRemove={handleUploadRemove}
                        onUploadFile={handleUploadFile}
                        onUploadOpen={handleUploadOpen}
                        layoutVariant="ROW_C_FULL_PLAYBOOK"
                        expandAllTick={expandAllTick}
                        collapseAllTick={collapseAllTick}
                        defaultExpandLegalDetails={defaultExpandLegalDetails}
                      />
                    );
                  })
                )}
              </div>

              <button
                type="button"
                onClick={handleScrollToTop}
                className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-2 text-xs font-black text-white shadow-lg shadow-slate-900/20 transition hover:bg-slate-800"
                aria-label="Return to Top of Page"
              >
                <span aria-hidden="true">↑</span>
                <span>Return to Top of Page</span>
              </button>

              <div className="mt-10 pb-8 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
